import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SupportIcon } from "@heroicons/react/outline";
import { AuthContext } from "context/Auth";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { SupportDialog } from "./SupportDialog";

const SupportLauncher = ({
  accountId,
  pagePath,
}: {
  accountId: string;
  pagePath: string;
}) => {
  const [open, setOpen] = useState(false);
  const [footerHeight, setFooterHeight] = useState(80);
  const { t } = useTranslation();

  useEffect(() => {
    const footer = document.querySelector("footer");
    const measure = () =>
      setFooterHeight(footer?.getBoundingClientRect().height || 0);
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : undefined;
    if (footer) observer?.observe(footer);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <>
      <div
        hidden={open}
        className="fixed right-5 z-10 rounded-md shadow-lg"
        style={{ bottom: footerHeight + 20 }}
      >
        <PrimaryButton onClick={() => setOpen(true)}>
          <span className="inline-flex items-center gap-2">
            <SupportIcon className="h-5 w-5" aria-hidden="true" />
            {t("support.help")}
          </span>
        </PrimaryButton>
      </div>
      {open && (
        <SupportDialog
          accountId={accountId}
          pagePath={pagePath}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export const SupportButton = () => {
  const { isAuthenticated, getUserInfo } = useContext(AuthContext);
  const { pathname } = useLocation();
  const user = getUserInfo();
  if (!isAuthenticated || !user.accountId) return null;
  // Replacing the session also discards pending results on navigation or identity changes.
  return (
    <SupportLauncher
      key={`${user.accountId}:${user.sub}:${pathname}`}
      accountId={user.accountId}
      pagePath={pathname}
    />
  );
};
